import PropTypes from 'prop-types'
import Modal from '../../common/Modal'
import classes from './PostAnnouncementModal.module.scss'
import { useSelector } from 'react-redux'
import { useState } from 'react'
import classNames from 'classnames'
import DualPanelSelector from '../../common/dual-panel-selector/DualPanelSelector'
import { selectProgramsData, selectSeriesData, selectStudentsData } from '../../../reducers/dualPanelSelectorReducer'
import SubmitButton from '../../common/buttons/SubmitButton'
import PostAnnouncementForm from './PostAnnouncementForm'
import { selectSelectedPrograms, selectSelectedStudents } from '../../../reducers/programDashboardReducer'
import { selectSelectedSeries, selectSelectedCoaches } from '../../../reducers/programDashboardReducer'
import { ReactComponent as WarningIcon } from '../../../assets/images/common/icons/warning-icon.svg'
import { ReactComponent as MailIcon } from '../../../assets/images/common/icons/envelope-icon.svg'
import ExitModal from '../../common/ExitModal/ExitModal'
import { useLeftPanelProps } from './useLeftPanelProps'
import { useRightPanelProps } from './useRightPanelProps'
import { useProgramsText } from './useProgramsText'
import { requestAnnouncementRecipients } from '../../../actions/activityActions'

const PostAnnouncementModal = ({ isOpen, onClose, isProgram, announcement, isEdit, programsText }) => {
  const [programForm, setProgramForm] = useState(false)
  const [exitModal, setExitModal] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [warningModal, setWarningModal] = useState(false)
  const programsData = useSelector(selectProgramsData)
  const selectedPrograms = useSelector(selectSelectedPrograms)
  const selectedCoaches = useSelector(selectSelectedCoaches)
  const seriesData = useSelector(selectSeriesData)
  const coachesData = useSelector(state => state.dualPanelSelector.coaches)
  const selectedSeries = useSelector(selectSelectedSeries)
  const studentsData = useSelector(selectStudentsData)
  const selectedStudents = useSelector(selectSelectedStudents)
  const current_user = useSelector(state => state.user.current_user)
  const communityId = current_user.communities[0].id
  const selectedStudentsMap = useSelector(state => state.programDashboard.addStudentsModal.selectedStudents.entities)
  const [allCoachesSelected, setAllCoachesSelected] = useState(false)
  const [allStudentsSelected, setAllStudentsSelected] = useState(false)


  const selectedUsers = [...selectedStudents, ...selectedCoaches]

  const selectedProgramsText = useProgramsText({
    selectedPrograms,
    selectedSeries,
    selectedUsers,
  })

  const handleClose = () => {
    onClose()
    setExitModal(false)
  }

  const downloadCSV = (filename, rows) => {
    const csvContent = [
      ['Name', 'Role', 'Email', 'Family Admin Emails'],
      ...rows.map(row => [
        row.name,
        row.role,
        row.email,
        (row.family_admin_emails || []).join(', '),
      ]),
    ]
      .map(e => e.map(field => `"${field}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleContactList = () => {
    const directStudentIds = selectedStudents.map(s => s.id)
    const programStudentIds = selectedPrograms.flatMap(program =>
      (program.students || []).map(s => s.id)
    )
    const seriesStudentIds = selectedSeries.flatMap(series =>
      (series.students || []).map(s => s.id)
    )
    const coachIds = selectedCoaches.map(c => c.id)

    const allIds = Array.from(
      new Set([...directStudentIds, ...programStudentIds, ...seriesStudentIds, ...coachIds])
    )

    if (allIds.length === 0) { return }


    requestAnnouncementRecipients(allIds).then(response => {
      const recipients = response.data.recipients || []

      const rows = recipients.map(r => ({
        name: r.name,
        role: r.role,
        email: r.email || 'N/A',
        family_admin_emails: r.family_admin_emails || [],
      }))

      downloadCSV('contact_list.csv', rows)
      setWarningModal(false)
    })
  }


  const handleCloseProgramForm = () => {
    setProgramForm(false)
  }

  const leftPanelProps = useLeftPanelProps({
    studentsData,
    seriesData,
    programsData,
    coachesData,
    communityId,
    selectedStudentsMap,
    allCoachesSelected,
    setAllCoachesSelected,
    allStudentsSelected,
    setAllStudentsSelected,
  })

  const rightPanelProps = useRightPanelProps(
    selectedPrograms,
    selectedSeries,
    selectedStudents,
    selectedCoaches
  )

  const handleModalClose = () => {
    if (isDirty) {
      setExitModal(true)
    } else {
      onClose()
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleModalClose} className={classNames(classes.modal)}>
        {<PostAnnouncementForm isProgram={isProgram} nextStep={setProgramForm} onClose={onClose} setIsDirty={setIsDirty}
          programsText={programsText || selectedProgramsText} isEdit={isEdit} announcement={announcement}
          warningModal={warningModal} setWarningModal={setWarningModal}/>}
      </Modal>
      <Modal isOpen={programForm} onClose={handleCloseProgramForm} className={classNames(classes.modal, classes.modalPrograms)}>
        <div className={classNames('d-flex flex-column', classes.modalContentWrapper)}>
          <DualPanelSelector
            type='ProgramRoster'
            className={classes.dualPanelSelector}
            {...leftPanelProps}
            {...rightPanelProps}/>
          <div className={classes.submitButtonContainer}>
            <div className={classes.button}>
              <SubmitButton disabled={false} buttonMessage='Continue' onClick={() => setProgramForm(false)}/>
            </div>
          </div>
        </div>
      </Modal>
      <ExitModal
        showModal={exitModal}
        closeModal={() => setExitModal(false)}
        onSubmit={handleClose}
        icon={<WarningIcon className={classes.warningIcon}/>}
        title='Attention!'
        message='Are you sure you want to stop creating your announcement? If you exit now, all your progress will be deleted.'
        submitButtonMessage='Back to Announcement'
      />

      <ExitModal
        showModal={warningModal}
        closeModal={handleContactList}
        onSubmit={() => setWarningModal(false)}
        icon={<div className={classes.envelopeIconContainer}><MailIcon className={classes.envelopeIcon}/> </div>}
        title='Email Limits for Operation 36!'
        messageStyle={classes.warningMessageStyle}
        message={`Our email tools are designed for day-to-day communication
—not large-scale marketing.\n
For larger messages, we recommend using Post an 
Announcement to reach all members through the app (push 
notification + feed visibility). It’s now the best way to deliver important updates—and even supports file attachments.\n
Need to send a mass email through your own tool?
You can download your contacts or contact our team to help sync your members to your own email service.`}
        submitButtonMessage='Download Contact List'
        exitButtonMessage={'Got it!'}
      />
    </>)
}

PostAnnouncementModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isProgram: PropTypes.bool,
  isEdit: PropTypes.bool,
  programsText: PropTypes.string,
  announcement: PropTypes.shape({
    id: PropTypes.number.isRequired,
    caption: PropTypes.string.isRequired,
    activity_date: PropTypes.string.isRequired,
    photo: PropTypes.shape({
      original_file_url: PropTypes.string,
    }),
    video: PropTypes.shape({
      attachment_url: PropTypes.string,
    }),
    courses: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
    series: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
      })
    ),
    users: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      })
    ),
    comment_count: PropTypes.number.isRequired,
    like_count: PropTypes.number,
    user: PropTypes.object.isRequired,
  }),
}

export default PostAnnouncementModal
